import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Center,
  Flex,
  Select,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import 'react-quill/dist/quill.snow.css';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useAddFaqMutation,
  useGetCategoriesQuery,
  useUpdateFaqMutation,
  useGetFaqByIdQuery,
  useAppSelector,
} from '@spartalabs/pdc-core';
import { Card, LoadingModal, SimpleInput, Unauthorized } from 'components';
import { useCustomToast } from 'hooks';
import { DelayAnimation } from 'shared/DelayAnimation';
import * as z from 'zod';
import { HtmlStlyed } from '../components/HtmlParser';
import { QuestionCard } from '../QuestionList/components/QuestionCard';
import { FaqPicker } from './components/Picker';

const FaqSchema = z.object({
  title: z.string().nonempty({ message: 'Campo obrigatório' }),
  desc: z.string().nonempty('Campo obrigatório').optional(),
  cat: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' })
    .refine(value => !value.includes('_'), 'Celular inválido'),
  htmldesc: z.string().nonempty({ message: 'Campo obrigatório' }),
});

type FaqSchemaProps = z.infer<typeof FaqSchema>;

export const FormQuestion: React.FC<{ isEdit?: boolean }> = ({ isEdit }) => {
  const { id } = useParams();

  const { data: editData } = useGetFaqByIdQuery({ id: id || '' });

  const [picked, setPicked] = useState('card');
  const { data, isLoading } = useGetCategoriesQuery();

  const [addFaq, { isLoading: addLoading }] = useAddFaqMutation();
  const [editFaq, { isLoading: editLoading }] = useUpdateFaqMutation();

  const { handleToast } = useCustomToast();

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
  } = useForm<FaqSchemaProps>({
    resolver: zodResolver(FaqSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const navigate = useNavigate();

  const quillModule = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'bullet' }, { list: 'ordered' }],
        ['link', { image: 'image' }, 'video'],
      ],
    },
  };

  const removeColors = (html: string) => {
    const colorRemoved = html.replace(/style="[^"]*"/gi, match => {
      return match
        .replace(/background-color:[^;"]*/gi, '')
        .replace(/color:[^;"]*/gi, '');
    });
    return colorRemoved;
  };

  const handleFormat = (text: string) => {
    setValue('htmldesc', removeColors(text));
  };

  const onSubmit = async (faq: FaqSchemaProps) => {
    try {
      isEdit
        ? await editFaq({
            id: id || '',
            index: 0,
            faqCategorieId: faq.cat,
            title: faq.title,
            desc: faq.desc,
            htmlContent: faq.htmldesc,
          }).unwrap()
        : await addFaq({
            id: '',
            index: 0,
            faqCategorieId: faq.cat,
            title: faq.title,
            desc: faq.desc,
            htmlContent: faq.htmldesc,
          }).unwrap();

      navigate('/centraldeduvidas/editarcategorias');
    } catch {
      handleToast({
        step: 4,
        action: 'criar o FAQ',
      });
    }
  };

  useEffect(() => {
    if (editData) {
      setValue('title', editData.title);
      setValue('cat', editData.faqCategorieId);
      setValue('htmldesc', editData.htmlContent);
      setValue('desc', editData.desc);
    }
  }, [editData]);

  if (!permissions?.includes('faq')) return <Unauthorized />;

  return (
    <DelayAnimation>
      <LoadingModal isOpen={isLoading} />
      <Box mt="40px" mx="34px">
        <Flex
          bg="white"
          py="24px"
          px="18px"
          rounded="base"
          justify="space-between"
          align="center"
          mb="20px"
        >
          <Button
            onClick={() => navigate('/centraldeduvidas/editarcategorias')}
            variant="outline"
          >
            Voltar
          </Button>
          <Center w="100%">
            <Text fontSize="18px" color="GrayText">
              {isEdit ? 'Editar conteúdo' : 'Criar conteúdo'}
            </Text>
          </Center>
        </Flex>

        <Flex gap="24px" flexDirection={{ base: 'column', lg: 'row' }}>
          <Card w={{ base: '100%', lg: '60%' }} py="32px" px="18px">
            <Flex flexDir="column" gap="24px">
              <Flex alignItems="top" gap="40px">
                <SimpleInput
                  maxLength={95}
                  {...register('title')}
                  size="lg"
                  borderColor="grey.500"
                  color="text.100"
                  fontSize="14px"
                  fontWeight="500"
                  _placeholder={{ textColor: 'text.100' }}
                  placeholder="Titulo"
                  labelColor="text.100"
                  label="*Titulo"
                  error={errors.title?.message}
                />

                <Box w="60%" display={{ base: 'none', lg: 'block' }}>
                  <Text
                    mb="4px"
                    aria-hidden="true"
                    fontSize="sm"
                    fontWeight={400}
                    color="text.100"
                    alignSelf="flex-start"
                  >
                    *Categoria
                  </Text>

                  <Select
                    textColor="text.100"
                    size="lg"
                    fontSize="14px"
                    borderColor="grey.500"
                    placeholder="Escolha uma categoria"
                    onChange={event => setValue('cat', event.target.value)}
                    value={watch('cat')}
                  >
                    {data?.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </Select>

                  {errors.cat?.message && (
                    <>
                      <VisuallyHidden>
                        <Text>Erro, {errors.cat?.message}</Text>
                      </VisuallyHidden>
                      <Text
                        aria-hidden="true"
                        alignSelf="start"
                        pl="8px"
                        color="red.200"
                        fontSize="sm"
                      >
                        {errors.cat?.message}
                      </Text>
                    </>
                  )}
                </Box>
              </Flex>
              <SimpleInput
                maxW={{ base: '100%', lg: '77%' }}
                maxLength={93}
                onFocus={() => setPicked('card')}
                {...register('desc')}
                size="lg"
                borderColor="grey.500"
                color="text.100"
                fontSize="14px"
                fontWeight="500"
                _placeholder={{ textColor: 'text.100' }}
                placeholder="Descrição"
                labelColor="text.100"
                label="*Descrição"
                error={errors.desc?.message}
              />

              <Box display={{ base: 'block', lg: 'none' }}>
                <Text
                  mb="4px"
                  aria-hidden="true"
                  fontSize="sm"
                  fontWeight={400}
                  color="text.100"
                  alignSelf="flex-start"
                >
                  Categoria
                </Text>

                <Select
                  textColor="text.100"
                  size="lg"
                  fontSize="14px"
                  borderColor="grey.500"
                  placeholder="Escolha uma categoria"
                  onChange={event => setValue('cat', event.target.value)}
                  value={watch('cat')}
                >
                  {data?.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </Select>

                {errors.cat?.message && (
                  <>
                    <VisuallyHidden>
                      <Text>Erro, {errors.cat?.message}</Text>
                    </VisuallyHidden>
                    <Text
                      aria-hidden="true"
                      alignSelf="start"
                      pl="8px"
                      color="red.200"
                      fontSize="sm"
                    >
                      {errors.cat?.message}
                    </Text>
                  </>
                )}
              </Box>

              <Box>
                <Text
                  mb="4px"
                  aria-hidden="true"
                  fontSize="sm"
                  fontWeight={400}
                  color="text.100"
                  alignSelf="flex-start"
                >
                  *Conteúdo do tópico
                </Text>

                <ReactQuill
                  onFocus={() => setPicked('html')}
                  modules={quillModule}
                  style={{
                    color: 'black',
                  }}
                  value={watch('htmldesc')}
                  onChange={handleFormat}
                />

                {errors.htmldesc?.message && (
                  <>
                    <VisuallyHidden>
                      <Text>Erro , campo obrigatório</Text>
                    </VisuallyHidden>
                    <Text
                      aria-hidden="true"
                      alignSelf="start"
                      pl="8px"
                      color="red.200"
                      fontSize="sm"
                    >
                      Campo obrigatório
                    </Text>
                  </>
                )}
              </Box>
              <Flex
                display={{ base: 'none', lg: 'flex' }}
                alignSelf="end"
                gap="12px"
              >
                <Button
                  isLoading={addLoading || editLoading}
                  onClick={handleSubmit(onSubmit)}
                  mt="40px"
                  w={{ base: '100%', md: '120px' }}
                >
                  Concluir
                </Button>
              </Flex>
            </Flex>
          </Card>

          <Card justifyContent="top" w={{ base: '100%', lg: '40%' }}>
            <Flex
              alignItems="center"
              mt={{ base: '24px', lg: '0px' }}
              flexDir="column"
            >
              <FaqPicker setPicked={setPicked} picked={picked} />

              {picked === 'card' ? (
                <QuestionCard
                  title={watch('title') || 'Insira um titulo'}
                  description={watch('desc') || 'Insira uma descrição'}
                />
              ) : (
                <HtmlStlyed
                  value={watch('htmldesc') || 'Insira um conteúdo'}
                  title={watch('title') || 'Insira um titulo'}
                />
              )}
            </Flex>
            <Flex
              justifyContent="center"
              display={{ base: 'flex', lg: 'none' }}
              gap="12px"
            >
              <Button
                isLoading={addLoading || editLoading}
                onClick={handleSubmit(onSubmit)}
                mt="40px"
                w="80%"
              >
                Concluir
              </Button>
            </Flex>
          </Card>
        </Flex>
      </Box>
    </DelayAnimation>
  );
};
